/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import env from '@lib/envs/env';
import DefaultTemplate from '@components/shared/templates/DefaultTemplate/DefaultTemplate';
import { IDesignerHighlight } from '@components/pages/ShopLanding/model/designerHighlight';
import DesignerHighlight
  from '@components/pages/ShopLanding/components/DesignerHighlight/DesignerHighlight';
import { useRouter } from 'next/router';
import { useTheme } from '@components/theme';
import useWindowSize from '@hooks/useWindowSize/useWindowSize';
import breakpoints from '@constants/breakpoints';
import ShopNavigationMenu, {
  IShopNavigationMenu
} from '@components/shared/shop/ShopNavigationMenu';
import { FeaturedBoards as FeatureBoardsModel } from '@components/shared/FeaturedBoards/model';
import FeaturedBoards from '@components/shared/FeaturedBoards/FeaturedBoards';
import { SHOP_SEARCH_URL } from '@components/pages/ShopV3/util/search';
import { AvailabilityStatus } from '@models/VendorVariant/i-vendor-variant';
import { Vendor, VendorTieredDiscountCodeModel } from '@codegen-basetypes';
import HeroBanner from './components/HeroBanner/HeroBanner';
import OurStories from '../../shared/OurStories/OurStories';
import { IHeroBanner, IOurStories, IRaisonDetre } from './model/prismic';
import { ICollectionFeature } from './model/collectionFeature';
import styles from './ShopLanding.module.scss';
import Footer from './components/Footer/Footer';
import CollectionFeature from './components/CollectionFeature/CollectionFeature';
import FeaturedMerchGroups from './components/FeaturedMerchGroups/FeaturedMerchGroups';
import { IFeaturedMerchGroups } from './model/featuredMerchGroups';
import { ICollectionWithProductCards } from './model/collectionWithProductCards';
import CollectionWithProductCards
  from './components/CollectionWithProductCards/CollectionWithProductCards';
import RaisonDetre from './components/RaisonDetre/RaisonDetre';

export interface IVendorVariant {
  id: string;
  title: string;
  currentPrice: number;
  availability: AvailabilityStatus;
  price: number;
  retailPrice: {
    price: number;
  };
  saleOnSalePrice: {
    price: number;
    percentage: number;
  };
  image: {
    uri: string;
  };
  images: Array<{
    uri: string;
  }>;
  productUrl: string;
  vendor: Vendor;
  vendorTieredDiscountCode?: VendorTieredDiscountCodeModel;
}

interface IShopLandingProps {
  shopNavigationMenu: IShopNavigationMenu[];
  heroBanner: IHeroBanner;
  collectionFeature: ICollectionFeature;
  raisonDetre: IRaisonDetre;
  collectionWithProductCards: ICollectionWithProductCards;
  ourStories: IOurStories[];
  featuredBoards: FeatureBoardsModel;
  featuredMerchGroups: IFeaturedMerchGroups;
  designerHighlight: IDesignerHighlight;
}

const ShopLanding = ({
  shopNavigationMenu,
  heroBanner,
  collectionFeature,
  raisonDetre,
  collectionWithProductCards,
  ourStories,
  featuredMerchGroups,
  featuredBoards,
  designerHighlight,
}: IShopLandingProps) => {
  const theme = useTheme();
  const [unbounceBannerHeight, setUnbounceBannerHeight] = useState(0);

  const router = useRouter();

  const { width: windowWidth } = useWindowSize();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mobile = windowWidth && windowWidth < breakpoints.desktop;
    setIsMobile(!!mobile);
  }, [windowWidth]);

  return (
    <DefaultTemplate
      canonicalLink={`https://${env.DOMAIN_NAME}/shop`}
      description="Browse furniture & décor from 200+ retailers in the Havenly Shop."
      isNavVisible
      isFooterVisible
      navProps={{
        isFixed: true,
        isTransparent: Boolean(isMobile),
        shopShopMegaMenu: false,
        shopNavigationMenu,
        showSearch: Boolean(isMobile),
        onSearch: (search: string) => {
          const text = search?.trim();
          router.push({
            pathname: SHOP_SEARCH_URL,
            query: {
              text
            }
          });
        },
      }}
      title="Shop | Havenly"
      className={theme.styles.Body}
      fonts={theme.fontLinks}
      checkUnbounceBannerHeight={
        (height) => setUnbounceBannerHeight(height)
      }
    >
      <div className={styles.ShopLanding}>
        <ShopNavigationMenu
          shopNavigationMenu={shopNavigationMenu}
          unbounceBannerHeight={unbounceBannerHeight}
          isFixed
        />
        <HeroBanner {...heroBanner} />
        <FeaturedMerchGroups {...featuredMerchGroups} />
        <RaisonDetre {...raisonDetre} />
        <FeaturedBoards featuredBoards={{ ...featuredBoards }} source="shop_home" />
        <CollectionFeature {...collectionFeature} />
        <DesignerHighlight {...designerHighlight} />
        <CollectionWithProductCards {...collectionWithProductCards} />
        {!!ourStories?.length &&
          <OurStories className={styles.OurStories} blogLinks={ourStories} />}
        <Footer />
      </div>

    </DefaultTemplate>
  );
};

export default ShopLanding;
